import { createSignal, onMount, Show } from "solid-js";
import { BRAND } from "./brand.ts";
import {
  type Account,
  askForLink,
  finishSignIn,
  forgetMe,
  renameMe,
  signOut,
  whoAmI,
} from "./auth.ts";
import { makerName } from "./maker.ts";
import { type PushState, pushState, turnPushOff, turnPushOn } from "./push.ts";

/**
 * Who you are, in the corner of a page.
 *
 * Signing in is worth doing here for exactly two reasons: your work stays yours on a second
 * device, and a name on a shelf becomes one somebody had to prove. Neither is urgent enough to
 * stand in front of anything, so this never blocks the page and never asks twice.
 */
export function Me() {
  const [account, setAccount] = createSignal<Account | null>(null);
  const [open, setOpen] = createSignal(false);
  const [email, setEmail] = createSignal("");
  const [name, setName] = createSignal(makerName());
  const [note, setNote] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [news, setNews] = createSignal(false);
  const [sure, setSure] = createSignal(false);
  const [told, setTold] = createSignal<PushState>("off");

  onMount(() => {
    const asked = new URLSearchParams(location.search).get("in");
    if (asked !== null && asked !== "") {
      setBusy(true);
      void finishSignIn(asked).then((found) => {
        setBusy(false);
        if (found === null) {
          setNote("That link has been used or has expired. Ask for another.");
          setOpen(true);
          return;
        }
        setAccount(found);
        setName(found.name);
        // The token is spent, and an address with a token in it is one somebody might share.
        history.replaceState(null, "", location.pathname);
      });
      return;
    }
    void whoAmI().then(setAccount);
    void pushState().then(setTold);
  });

  const send = async (): Promise<void> => {
    setBusy(true);
    setNote("");
    const how = await askForLink(email().trim(), news());
    setBusy(false);
    if (how === "posted") setNote("Check your mail. The link works for fifteen minutes.");
    else if (how === "nopost") setNote("Signing in is not switched on here yet.");
    else setNote("That does not look like an address.");
  };

  const rename = async (): Promise<void> => {
    setBusy(true);
    const kept = await renameMe(name());
    setBusy(false);
    setAccount({ name: kept, email: account()?.email ?? "" });
    setNote("Saved.");
  };

  return (
    <div class="me">
      <Show
        when={account() !== null}
        fallback={
          <>
            <button class="me__who" onClick={() => setOpen(!open())}>
              Sign in
            </button>
            <Show when={open()}>
              <div class="me__panel">
                <label for="me-email">Your email. No password, ever.</label>
                <div class="me__row">
                  <input
                    id="me-email"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    placeholder="you@example.com"
                    value={email()}
                    onInput={(event) => setEmail(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void send();
                    }}
                  />
                  <button disabled={busy()} onClick={() => void send()}>
                    Send a link
                  </button>
                </div>
                {/* Unticked, and separate from signing in, because those are two different asks. */}
                <label class="me__opt">
                  <input
                    type="checkbox"
                    checked={news()}
                    onChange={(event) => setNews(event.currentTarget.checked)}
                  />
                  Write to me now and then about what is new. Never anything else.
                </label>
                <Show when={note() !== ""}>
                  <p class="me__note">{note()}</p>
                </Show>
              </div>
            </Show>
          </>
        }
      >
        <button class="me__who" onClick={() => setOpen(!open())}>
          {account()?.name === "" ? "Signed in" : account()?.name}
        </button>
        <Show when={open()}>
          <div class="me__panel">
            {" "}
            <label for="me-name">The name on what you make</label>
            <div class="me__row">
              <input
                id="me-name"
                value={name()}
                placeholder="Anonymous"
                onInput={(event) => setName(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void rename();
                }}
              />
              <button disabled={busy()} onClick={() => void rename()}>
                Save
              </button>
            </div>
            <Show when={note() !== ""}>
              <p class="me__note">{note()}</p>
            </Show>
            <label class="me__opt">
              <input
                type="checkbox"
                checked={told() === "on"}
                disabled={told() === "unsupported" || told() === "blocked" || told() === "nokey"}
                onChange={() => {
                  void (told() === "on" ? turnPushOff() : turnPushOn()).then(setTold);
                }}
              />
              {told() === "unsupported"
                ? `This browser cannot be told anything. On iPhone, add ${BRAND} to your home screen first.`
                : told() === "blocked"
                  ? "Notifications are turned off for this site in your browser settings."
                  : told() === "nokey"
                    ? "Being told is not switched on here yet."
                    : "Tell me when somebody beats my score."}
            </label>
            <button
              class="me__out"
              onClick={() => {
                void signOut().then(() => {
                  setAccount(null);
                  setOpen(false);
                });
              }}
            >
              Sign out
            </button>
            <Show
              when={sure()}
              fallback={
                <button class="me__gone" onClick={() => setSure(true)}>
                  Delete my account
                </button>
              }
            >
              <p class="me__warn">
                This removes your account and takes your name off anything you published. Games you
                shared stay where they are. It cannot be undone.
              </p>
              <button
                class="me__gone me__gone--now"
                onClick={() => {
                  void forgetMe().then((done) => {
                    if (!done) {
                      setNote("That did not work. Try again.");
                      return;
                    }
                    setAccount(null);
                    setSure(false);
                    setOpen(false);
                  });
                }}
              >
                Yes, delete it
              </button>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}
